import { emitHintSignal } from '@flobi/asset-library/hints';
import { toParent } from '../space';
import { ROOT_ID, type Rect } from '@flobi/artwork-core';
import type { IconName } from '../chrome/icons';
import type { Point, RailButton, Tool, ToolContext } from './types';

/**
 * Slices: the parts of the page that come out as files of their own.
 *
 * Illustrator's Slice tool, and the same idea every editor since has landed on. A slice is
 * a rectangle drawn over the artwork saying "this is one file" — not a layer, not a group,
 * a WINDOW. That is the whole of why it exists: what a person wants to export almost never
 * lines up with how the artwork was built. A badge that straddles two groups is one file; a
 * sheet of icons is twenty; a layer that runs off the edge is trimmed to what the window
 * shows. Nothing about the layer tree can say any of that, so a slice says it instead.
 *
 * It draws nothing. A slice that could be filled would be a rectangle with an odd name,
 * and the point of it is that it is not on the page — it is a question asked of the page.
 * So it is drawn here, on the overlay, where guides and handles live, and only while one
 * of these two is in hand.
 */

type Mode = 'draw' | 'choose';

const MODES: { id: Mode; icon: IconName; title: string }[] = [
  { id: 'draw', icon: 'slice', title: 'Draw slice — drag out a part of the page to export as its own file' },
  { id: 'choose', icon: 'select-object', title: 'Select slice — choose one to move, size or delete' },
];

/** How near a slice's edge or corner counts as being, in screen pixels. */
const REACH = 8;
/** Smaller than this and it was a press, not a slice. */
const LEAST = 4;

type Grip = 'move' | 'nw' | 'ne' | 'se' | 'sw';

export function sliceTool(): Tool {
  let mode: Mode = 'draw';
  let drawing: { from: Point; to: Point } | null = null;
  let held: { id: string; grip: Grip; from: Point; was: Rect } | null = null;

  /** Every slice on the page, with its box in page coordinates. */
  const slices = (ctx: ToolContext): { id: string; box: Rect; name: string }[] =>
    ctx.scene
      .childrenOf(ROOT_ID)
      .flatMap((id) => [id, ...ctx.scene.childrenOf(id)])
      .flatMap((id) => {
        const node = ctx.node(id);
        if (node?.type !== 'slice') return [];
        const box = ctx.boundsOf(id);
        return box ? [{ id, box, name: node.name }] : [];
      });

  const cornersOf = (box: Rect): Record<Exclude<Grip, 'move'>, Point> => ({
    nw: { x: box.x, y: box.y },
    ne: { x: box.x + box.width, y: box.y },
    se: { x: box.x + box.width, y: box.y + box.height },
    sw: { x: box.x, y: box.y + box.height },
  });

  const holds = (box: Rect, at: Point) =>
    at.x >= box.x && at.x <= box.x + box.width && at.y >= box.y && at.y <= box.y + box.height;

  /** Which slice a press lands on, and by which grip. Topmost first, as a press expects. */
  const under = (ctx: ToolContext, world: Point): { id: string; grip: Grip; box: Rect } | null => {
    const reach = REACH * ctx.unitsPerPixel;
    const found = slices(ctx).reverse();
    for (const slice of found) {
      const corners = cornersOf(slice.box);
      for (const key of ['nw', 'ne', 'se', 'sw'] as const) {
        const at = corners[key];
        if (Math.hypot(at.x - world.x, at.y - world.y) <= reach) return { id: slice.id, grip: key, box: slice.box };
      }
    }
    // Inside, the smallest slice holding the point: an outer slice drawn later always won,
    // so an inner one could only be moved by its corners (QA 2026-09-24, cut-slice-measure-F17).
    let inside: { id: string; box: Rect } | null = null;
    for (const slice of found) {
      if (holds(slice.box, world) && (!inside || slice.box.width * slice.box.height < inside.box.width * inside.box.height)) inside = slice;
    }
    return inside ? { id: inside.id, grip: 'move', box: inside.box } : null;
  };

  const between = (a: Point, b: Point): Rect => ({
    x: Math.min(a.x, b.x),
    y: Math.min(a.y, b.y),
    width: Math.abs(b.x - a.x),
    height: Math.abs(b.y - a.y),
  });

  const write = (ctx: ToolContext, id: string, box: Rect): void => {
    // The box is measured on the page; the slice stores it in its parent's space.
    ctx.edit([{ op: 'set', id, props: { transform: toParent(ctx.scene, ctx.scene.parentOf(id), [1, 0, 0, 1, box.x, box.y]), width: box.width, height: box.height } }]);
  };

  return {
    id: 'slice',

    deactivate(ctx) {
      drawing = null;
      // A tool change mid-drag closes the step, or the next edit joined this move's undo
      // step (QA 2026-09-24, cut-slice-measure-F22).
      if (held) ctx.stopCapturing();
      held = null;
      ctx.setCursor(null);
    },

    onPointerDown(event, rawWorld, ctx) {
      // A drag the system interrupted never released, and the next drag on empty canvas
      // moved that old slice (QA 2026-09-24, cut-slice-measure-F23).
      held = null;
      const world = ctx.place(rawWorld);
      // ⌥ in Draw starts a new slice even over one, so a small slice can be drawn inside
      // a bigger one (QA 2026-09-24, cut-slice-measure-F17).
      const found = mode === 'draw' && event.altKey ? null : under(ctx, rawWorld);
      // A press on a slice takes hold of it whichever of the two is in hand: a person who
      // has just drawn one and wants it a little bigger should not have to change tool to
      // say so. Choosing is the mode that does nothing ELSE.
      if (found) {
        held = { id: found.id, grip: found.grip, from: rawWorld, was: found.box };
        ctx.setSelection([found.id]);
        ctx.refresh();
        return true;
      }
      if (mode === 'choose') {
        ctx.setSelection([]);
        ctx.refresh();
        return true;
      }
      drawing = { from: world, to: world };
      return true;
    },

    onPointerMove(event, rawWorld, ctx) {
      if (event.buttons === 0) {
        const found = mode === 'draw' && event.altKey ? null : under(ctx, rawWorld);
        ctx.setCursor(found ? (found.grip === 'move' ? 'move' : 'nwse-resize') : mode === 'draw' ? 'crosshair' : null);
        return;
      }
      if (held) {
        // A slice says where a file's edges are, so it is worth as much care as the
        // artwork: its corners land on the same guides and edges everything else does.
        const world = ctx.place(rawWorld, [held.id]);
        const dx = world.x - held.from.x;
        const dy = world.y - held.from.y;
        const was = held.was;
        if (held.grip === 'move') {
          write(ctx, held.id, { ...was, x: was.x + dx, y: was.y + dy });
        } else {
          // Sized from the corner opposite the one being dragged, which stays put.
          const corners = cornersOf(was);
          const ACROSS = { nw: 'se', ne: 'sw', se: 'nw', sw: 'ne' } as const;
          const fixed = corners[ACROSS[held.grip]];
          const dragged = { x: corners[held.grip].x + dx, y: corners[held.grip].y + dy };
          const box = between(fixed, dragged);
          // No smaller than a slice can be drawn: dragged onto its opposite corner, one
          // collapsed to nothing and exported an empty file (QA 2026-09-24,
          // cut-slice-measure-F21). It grows away from the fixed corner, whichever side
          // the hand is on.
          const least = LEAST * ctx.unitsPerPixel;
          if (box.width < least) {
            box.width = least;
            box.x = dragged.x < fixed.x ? fixed.x - least : fixed.x;
          }
          if (box.height < least) {
            box.height = least;
            box.y = dragged.y < fixed.y ? fixed.y - least : fixed.y;
          }
          write(ctx, held.id, box);
        }
        ctx.refresh();
        return;
      }
      if (!drawing) return;
      drawing = { from: drawing.from, to: ctx.place(rawWorld) };
      ctx.refresh();
    },

    onPointerUp(_event, rawWorld, ctx) {
      const world = ctx.place(rawWorld);
      if (held) {
        held = null;
        ctx.stopCapturing();
        return;
      }
      if (!drawing) return;
      const box = between(drawing.from, world);
      drawing = null;
      if (box.width < LEAST * ctx.unitsPerPixel || box.height < LEAST * ctx.unitsPerPixel) {
        ctx.refresh();
        return;
      }
      // One past the highest number in use, not the count: after a delete the count named
      // a new slice the same as one still there (QA 2026-09-24, cut-slice-measure-F18).
      const count = slices(ctx).reduce((most, slice) => Math.max(most, Number(/^Slice (\d+)$/.exec(slice.name)?.[1] ?? 0)), 0) + 1;
      const result = ctx.edit([
        {
          op: 'create',
          ref: 'slice',
          parentId: ROOT_ID,
          node: { type: 'slice', name: `Slice ${count}`, transform: [1, 0, 0, 1, box.x, box.y], width: box.width, height: box.height },
        },
      ]);
      if (result.ok && result.created.slice) {
        ctx.setSelection([result.created.slice]);
        // A slice marked: the hint that offers slices is done (lib/hints).
        emitHintSignal('slice.made');
      }
      ctx.stopCapturing();
      ctx.refresh();
    },

    onKeyDown(event, ctx) {
      if (event.key !== 'Backspace' && event.key !== 'Delete') return false;
      const gone = ctx.selection.filter((id) => ctx.node(id)?.type === 'slice');
      if (gone.length === 0) return false;
      event.preventDefault();
      ctx.edit(gone.map((id) => ({ op: 'delete', id })));
      ctx.setSelection([]);
      ctx.stopCapturing();
      ctx.refresh();
      return true;
    },

    rail(ctx) {
      const items: RailButton[] = MODES.map((item) => ({
        id: item.id,
        icon: item.icon,
        title: item.title,
        pressed: mode === item.id,
        run: () => {
          mode = item.id;
          drawing = null;
          ctx.railChanged();
        },
      }));
      return { top: { kind: 'buttons' as const, items } };
    },

    /**
     * Every slice, and the one being drawn.
     *
     * Named where they sit, because the name is the file name — a page of eight slices is
     * eight files, and which is which is the only thing a person needs to know about them
     * at a glance.
     */
    draw(overlay, ctx) {
      const chosen = new Set(ctx.selection);
      for (const slice of slices(ctx)) {
        overlay.box(slice.box, { width: chosen.has(slice.id) ? 2 : 1, dashed: true });
        if (!chosen.has(slice.id)) continue;
        for (const at of Object.values(cornersOf(slice.box))) overlay.handle(at, { filled: true });
      }
      if (drawing) overlay.box(between(drawing.from, drawing.to), { fill: true, alpha: 0.12, dashed: true });
    },
  };
}
